import * as React from 'react';
import { Box, Card, CardContent, Typography, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    DateField,
    NumberField,
    TextField,
    useListContext,
    RaRecord,
    RecordContextProvider,
    ReferenceField,
} from 'react-admin';
import { useFuneralDrawer } from './FuneralDrawerContext';
import { Funeral } from '../types';
import { ContractStatusChips } from '../components/ContractStatusChips';
import { LiveSearchBox } from '../components/LiveSearchBox';
import { CremationStatusField } from '../components/CremationStatusField';
import { BenefitsStatusField, BenefitsStatusProvider } from './BenefitsStatusField';

interface MobileGridProps {
    data?: RaRecord[];
}

const StatusIcon = ({ value }: { value: boolean }) => (
    value ? 
        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} /> : 
        <CancelIcon sx={{ color: '#e0e0e0', fontSize: 20 }} />
);

const MobileFuneralList = (props: MobileGridProps) => {
    const { data, isLoading } = useListContext<Funeral>();
    const drawer = useFuneralDrawer();

    if (isLoading || data.length === 0) {
        return null;
    }

    const handleCardClick = (funeralId: string | number) => (e: React.MouseEvent) => {
        // Don't open if clicking on interactive elements
        const target = e.target as HTMLElement;
        if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
            return;
        }
        drawer?.openFuneral(funeralId);
    };
    
    return (
        <>
        <Box sx={{ p: 2, pb: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <LiveSearchBox />
        </Box>
        <Box sx={{ p: 1 }}>
            <BenefitsStatusProvider funeralIds={data.map(r => r.id)}>
            {data.map(record => (
                <RecordContextProvider key={record.id} value={record}>
                    <Card 
                        sx={{ 
                            mb: 1.5,
                            backgroundColor: record.non_paperman_service ? '#ffeaba' : 'white',
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: 2
                            }
                        }}
                        onClick={handleCardClick(record.id)}
                    >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            {/* Header - ID and Names */}
                            <Box mb={1.5}>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    #{<NumberField source="id" options={{ useGrouping: false }} />}
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                        lineHeight: 1.3,
                                        mt: 0.25,
                                        mb: 0.25
                                    }}
                                >
                                    <ReferenceField
                                        source="deceased_client_id"
                                        reference="clients"
                                        link={false}
                                    >
                                        <TextField source="name" />
                                    </ReferenceField>
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <ReferenceField
                                        source="primary_client_id"
                                        reference="clients"
                                        link={false}
                                    >
                                        <TextField source="name" />
                                    </ReferenceField>
                                </Typography>
                            </Box>

                            {/* Date of Death */}
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    mb: 1.5,
                                    py: 1,
                                    px: 1.5,
                                    bgcolor: 'grey.50',
                                    borderRadius: 1
                                }}
                            >
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        mr: 1
                                    }}
                                >
                                    DOD:
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        fontWeight: 600,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <DateField
                                        source="date_time_of_death"
                                        options={{
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                        }}
                                    />
                                </Typography>
                            </Box>

                            {/* Contracts */}
                            <Box mb={1.5}>
                                <ContractStatusChips />
                            </Box>

                            <Divider sx={{ my: 1.5 }} />

                            {/* Cremation Status - Separate */}
                            <Box mb={1.5}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Cremation Status
                                </Typography>
                                <CremationStatusField source="cremation_status" />
                            </Box>

                            {/* Status Grid - Compact with icons */}
                            <Box 
                                sx={{ 
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 1.5,
                                    rowGap: 1
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1}>
                                    <StatusIcon value={record.preneed_registry} />
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                        Registry
                                    </Typography>
                                </Box>
                                
                                <Box display="flex" alignItems="center" gap={1}>
                                    <StatusIcon value={!!record.dec_ready} />
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                        DEC
                                    </Typography>
                                </Box>
                                
                                <Box display="flex" alignItems="center" gap={1}>
                                    <StatusIcon value={record.attestation_ready} />
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                        Attestation
                                    </Typography>
                                </Box>

                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <BenefitsStatusField size="sm" />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </RecordContextProvider>
            ))}
            </BenefitsStatusProvider>
        </Box>
        </>
    );
};

MobileFuneralList.defaultProps = {
    data: {},
    ids: [],
};

export default MobileFuneralList;